'use client';

import Link from 'next/link';

const LAST_UPDATED = 'March 28, 2026';
const COMPANY_NAME = 'Commercial Law Publishers (India) Pvt. Ltd.';
const APP_NAME = 'Crack AIBE';
const CONTACT_EMAIL = 'info@crackaibe.com';
const WEBSITE = 'https://crackaibe.com';
const CONTACT_PHONE = '+919899633171';

export default function RefundPolicyPage() {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <Link href="/" className="flex items-center gap-2 text-gray-800 hover:text-blue-600 transition-colors">
            <img
              src="/assets/images/Logo-1774427039733.png"
              alt="Crack AIBE Logo"
              className="h-8 w-auto object-contain"
            />
            <span className="font-semibold text-sm hidden sm:block">{APP_NAME}</span>
          </Link>
          <div className="flex items-center gap-4 text-sm">
            <Link href="/terms-and-conditions" className="text-blue-600 hover:text-blue-700 transition-colors">Terms &amp; Conditions</Link>
            <Link href="/privacy-policy" className="text-blue-600 hover:text-blue-700 transition-colors">Privacy Policy</Link>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="mb-10">
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3">Refund Policy</h1>
          <p className="text-sm text-gray-500">
            <span className="font-medium">Last Updated:</span> {LAST_UPDATED}
          </p>
          <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
            Please read this Refund Policy carefully before making a purchase on {APP_NAME}. By completing your payment, you acknowledge that you have read and agree to this policy.
          </div>
        </div>

        <div className="space-y-10 text-gray-700 text-sm leading-relaxed">

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3 pb-2 border-b border-gray-200">1. Nature of Purchase</h2>
            <p>
              {APP_NAME} offers a <strong>subscription plan</strong> of <strong>₹500 for 6 months</strong> (excluding applicable taxes). This grants you full access to the platform for a period of six months from the date of payment.
            </p>
            <p className="mt-3">
              Once payment is successfully completed, you will receive immediate access to the platform for the duration of your subscription period.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3 pb-2 border-b border-gray-200">2. General Refund Policy</h2>
            <p>
              Because {APP_NAME} is a digital platform and access is granted immediately upon payment, <strong>all purchases are final and non-refundable</strong> as a general rule.
            </p>
            <p className="mt-3">
              We do not offer automatic refunds. If you wish to cancel your subscription, please contact us at{' '}
              <a href={`mailto:${CONTACT_EMAIL}`} className="text-blue-600 hover:underline">{CONTACT_EMAIL}</a>.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3 pb-2 border-b border-gray-200">3. Case-by-Case Refund Consideration</h2>
            <p>
              While refunds are not automatically granted, we understand that exceptional circumstances may arise. Refund requests will be reviewed and considered on a <strong>case-by-case basis</strong> at our sole discretion.
            </p>
            <p className="mt-3">
              If you believe you have a valid reason for a refund, you must contact us directly at{' '}
              <a href={`mailto:${CONTACT_EMAIL}`} className="text-blue-600 hover:underline">{CONTACT_EMAIL}</a>{' '}
              to discuss your case. We will review your request and respond within a reasonable timeframe.
            </p>
            <p className="mt-3">
              Circumstances that <em>may</em> be considered include:
            </p>
            <ul className="list-disc list-inside mt-3 space-y-2 pl-2">
              <li>
                <strong>Verified technical failure:</strong> If you are completely unable to access the platform due to a confirmed issue on our end, and we are unable to resolve it within a reasonable time after you report it.
              </li>
              <li>
                <strong>Duplicate payment:</strong> If you were charged more than once for the same purchase due to a payment processing error.
              </li>
              <li>
                <strong>Unauthorised transaction:</strong> If you believe a payment was made without your authorisation, please contact us immediately so we can investigate.
              </li>
            </ul>
            <p className="mt-3">
              Submitting a request does not guarantee a refund. Each case will be evaluated individually based on the circumstances presented.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3 pb-2 border-b border-gray-200">4. Non-Refundable Circumstances</h2>
            <p>Refunds will <strong>not</strong> be issued in the following circumstances:</p>
            <ul className="list-disc list-inside mt-3 space-y-2 pl-2">
              <li>You changed your mind after completing the purchase.</li>
              <li>You did not use the platform after purchasing access.</li>
              <li>You found the content unsatisfactory after accessing it.</li>
              <li>Your account was suspended or terminated due to a violation of our <Link href="/terms-and-conditions" className="text-blue-600 hover:underline">Terms &amp; Conditions</Link>, including but not limited to account sharing, multi-device access violations, or prohibited conduct.</li>
              <li>You failed the AIBE or any other examination.</li>
              <li>The refund request is made without a valid or verifiable reason.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3 pb-2 border-b border-gray-200">5. No Cancellation Policy</h2>
            <p>
              Since {APP_NAME} operates on a subscription model of ₹500 for 6 months, once payment is made, your access is active for the full subscription period. We do not offer mid-term cancellations or pro-rated refunds.
            </p>
            <p className="mt-3">
              If you wish to stop using the platform, you may simply discontinue use. No cancellation request is necessary, and no refund will be issued solely on the basis of discontinuing use before the subscription period ends.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3 pb-2 border-b border-gray-200">6. How to Contact Us About a Refund</h2>
            <p>
              To discuss a potential refund, please reach out to us at:
            </p>
            <div className="mt-3 p-4 bg-gray-50 border border-gray-200 rounded-lg">
              <p>
                <span className="font-semibold text-gray-900">Email:</span>{' '}
                <a href={`mailto:${CONTACT_EMAIL}`} className="text-blue-600 hover:underline">{CONTACT_EMAIL}</a>
              </p>
              <p className="mt-2">
                <span className="font-semibold text-gray-900">Phone / WhatsApp:</span>{' '}
                <a href={`tel:${CONTACT_PHONE}`} className="text-blue-600 hover:underline">{CONTACT_PHONE}</a>
              </p>
            </div>
            <p className="mt-3">
              Please include the following details in your message:
            </p>
            <ul className="list-disc list-inside mt-3 space-y-2 pl-2">
              <li>Your registered email address.</li>
              <li>The date and amount of the payment.</li>
              <li>The Razorpay payment ID (available in your payment confirmation email).</li>
              <li>A clear description of the reason for your refund request.</li>
            </ul>
            <p className="mt-3">
              We will acknowledge your request and respond within a reasonable timeframe. Please note that contacting us does not guarantee a refund — each case is reviewed individually.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3 pb-2 border-b border-gray-200">7. Refund Processing</h2>
            <p>
              If a refund is approved following our review, it will be processed to the original payment method used for the transaction. Refunds are processed through Razorpay and typically take <strong>5–10 business days</strong> to reflect in your account, depending on your bank or card issuer.
            </p>
            <p className="mt-3">
              We are not responsible for delays caused by your bank or payment provider.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3 pb-2 border-b border-gray-200">8. Changes to This Policy</h2>
            <p>
              We reserve the right to modify this Refund Policy at any time. Changes will be effective upon posting to this page. Your continued use of the platform after any changes constitutes your acceptance of the updated policy.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3 pb-2 border-b border-gray-200">9. Contact Us</h2>
            <div className="p-5 bg-gray-50 border border-gray-200 rounded-lg space-y-2">
              <p><span className="font-semibold text-gray-900">Company:</span> {COMPANY_NAME}</p>
              <p>
                <span className="font-semibold text-gray-900">Email:</span>{' '}
                <a href={`mailto:${CONTACT_EMAIL}`} className="text-blue-600 hover:underline">{CONTACT_EMAIL}</a>
              </p>
              <p>
                <span className="font-semibold text-gray-900">Website:</span>{' '}
                <a href={WEBSITE} className="text-blue-600 hover:underline">{WEBSITE}</a>
              </p>
              <p>
                <span className="font-semibold text-gray-900">Phone / WhatsApp:</span>{' '}
                <a href={`tel:${CONTACT_PHONE}`} className="text-blue-600 hover:underline">{CONTACT_PHONE}</a>
              </p>
            </div>
          </section>

        </div>

        <div className="mt-12 pt-8 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-gray-400">
          <p>© {new Date()?.getFullYear()} {COMPANY_NAME}. All rights reserved.</p>
          <div className="flex items-center gap-4">
            <Link href="/terms-and-conditions" className="hover:text-gray-600 transition-colors">Terms &amp; Conditions</Link>
            <Link href="/privacy-policy" className="hover:text-gray-600 transition-colors">Privacy Policy</Link>
            <Link href="/login" className="hover:text-gray-600 transition-colors">Sign In</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
